import React from 'react';
import {Box, Typography, Grid} from '@mui/material';
import IndividualDashboardShell from '../components/IndividualDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, PageHeader, StatCard, ScoreRow, BenchmarkLegend} from '../components/widgets.comp';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedKpi, isSuppressedBenchmark, kpiNote} from '../../DashboardCommon/dashboardCards';
import { READINESS_BAND_COLORS, CONTEXT_BAND_COLORS } from '../constants';

const SCORE_OVERVIEW_SECTION_CODE = 'S_2';
const CARD_SECTIONS = [SCORE_OVERVIEW_SECTION_CODE];

const ScoreOverviewPage = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });

    const sectionData = dashboardData?.cards?.sections?.find(s => s.section_code === SCORE_OVERVIEW_SECTION_CODE);

    if (loading) {
        return (
            <IndividualDashboardShell>
                <CircularLoader />
            </IndividualDashboardShell>
        );
    }

    if (!dashboardData || !sectionData) {
        return (
            <IndividualDashboardShell>
                <NoDataAvailable />
            </IndividualDashboardShell>
        );
    }

    const cards = sectionData?.cards || [];
    const c1Card = cards.find(c => c.card_type === 'TitleCard');
    const c2Card = cards.find(c => c.card_type === 'ORGCard');
    const c3Card = cards.find(c => c.card_code === 'C_3');
    const c4Card = cards.find(c => c.card_code === 'C_4');
    const c5Card = cards.find(c => c.card_code === 'C_5' || c.card_type === 'BarChartBMCard');
    const c5aCard = cards.find(c => c.card_code === 'C_5a');

    // C1: Title card
    // Mockup titles (ID slide 80) when the card sends none.
    const title = c1Card?.title || 'My Score Overview';
    const subtitle = c1Card?.footer || c1Card?.header || 'What does my overall profile show?';

    // C2: Org details
    const infoStrip = c2Card?.table_objects?.map(obj => ({
        label: obj.column_name,
        value: obj.values?.[0]
    })) || [];

    // C3 & C4: KPI scores
    const myComposite = c3Card?.kpi_value != null ? Math.trunc(Number(c3Card.kpi_value)) : null;
    const myCompositeBand = c3Card?.kpi_band;
    // The class average is withheld when the class has fewer than 10 students (the student's own scores stay).
    const classAvgSuppressed = isSuppressedKpi(c4Card);
    const classAvg = !classAvgSuppressed && c4Card?.kpi_value != null ? Math.trunc(Number(c4Card.kpi_value)) : null;
    const classAvgOf = (item) => {
        const b = item.benchmark_values?.find((x) => x.name === 'Class avg.');
        if (!b) return classAvg;
        return isSuppressedBenchmark(b) || b.value == null ? null : b.value;
    };

    // C5: Areas (select first 3 items)
    const itemsC5 = c5Card?.items || [];
    const areasC5 = itemsC5.slice(0, 3).map(item => ({
        label: item.label,
        sub: item.code,
        score: Math.trunc(Number(item.value)),
        band: item.band,
        classAvg: classAvgOf(item)
    }));

    // C5a: Context Areas (select first 3 items)
    const itemsC5a = c5aCard?.items || [];
    const areasC5a = itemsC5a.slice(0, 3).map(item => ({
        label: item.label,
        sub: item.code,
        score: Math.trunc(Number(item.value)),
        band: item.band,
        classAvg: classAvgOf(item)
    }));

    // Strengths / Areas to grow — the datahouse counts them (ID.S_2.C_3a = areas in
    // band High, C_3b = areas in band Low). The count used to be computed here and
    // counted Moderate and Basic areas as "weaknesses", so a student with one
    // Moderate area and seven High ones showed "Weaknesses 1".
    const c3aCard = cards.find(c => c.card_code === 'C_3a');
    const c3bCard = cards.find(c => c.card_code === 'C_3b');
    const countOf = (card, band) => (card?.kpi_value != null && card.kpi_value !== ''
        ? Math.trunc(Number(card.kpi_value))
        : itemsC5.filter(item => item.band === band).length);
    const effectiveCount = countOf(c3aCard, 'High');
    const growCount = countOf(c3bCard, 'Low');

    return (
        <IndividualDashboardShell>
            <PageHeader
                title={title}
                subtitle={subtitle}
            />

            {/* Info strip */}
            {infoStrip.length > 0 && (
                <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, mb: 3}}>
                    <Grid container spacing={2}>
                        {infoStrip.map((i) => (
                            <Grid item xs={6} md={3} key={i.label}>
                                <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5}}>
                                    {i.label}
                                </Typography>
                                <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>{i.value}</Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* KPI cards */}
            <Grid container spacing={2.5} sx={{mb: 3}}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard label={c3Card?.kpi_name} value={myComposite} band={myCompositeBand} bandColorMap={READINESS_BAND_COLORS}/>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard label={c4Card?.kpi_name} value={classAvg ?? '—'} sub={kpiNote(c4Card)} band={classAvgSuppressed ? null : c4Card?.kpi_band} bandColorMap={READINESS_BAND_COLORS}/>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard topColor="#1B6B55" label="Strengths" value={effectiveCount} sub="areas"/>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard topColor="#E8730C" label="Weaknesses" value={growCount} sub="areas"/>
                </Grid>
            </Grid>

            <Grid container spacing={2.5} alignItems="flex-start">
                <Grid item xs={12}>
                    {/* Combined Container for C_5 and C_5a */}
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                        <Grid container spacing={3} alignItems="stretch">
                            {/* C_5 Column */}
                            <Grid item xs={12} md={c5aCard ? 5.75 : 12}>
                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1}}>
                                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{c5Card?.title}</Typography>
                                    <BenchmarkLegend bandColorMap={READINESS_BAND_COLORS}/>
                                </Box>
                                {areasC5.map((a, idx) => (
                                    <Box key={a.sub} sx={{ mb: idx === areasC5.length - 1 ? 0 : 2.5 }}>
                                        <ScoreRow label={a.label} score={a.score} band={a.band} marker={a.classAvg} bandColorMap={READINESS_BAND_COLORS} layout="stacked"/>
                                    </Box>
                                ))}
                            </Grid>

                            {/* Vertical Bar */}
                            {c5aCard && (
                                <Grid item xs={12} md={0.5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                                    <Box sx={{ width: '1px', backgroundColor: 'var(--brand-border, #E5E7EB)', alignSelf: 'stretch' }} />
                                </Grid>
                            )}

                            {/* C_5a Column */}
                            {c5aCard && (
                                <Grid item xs={12} md={5.75}>
                                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1}}>
                                        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{c5aCard.title}</Typography>
                                        <BenchmarkLegend bandColorMap={CONTEXT_BAND_COLORS}/>
                                    </Box>
                                    {areasC5a.map((a, idx) => (
                                        <Box key={a.sub} sx={{ mb: idx === areasC5a.length - 1 ? 0 : 2.5 }}>
                                            <ScoreRow label={a.label} score={a.score} band={a.band} marker={a.classAvg} bandColorMap={CONTEXT_BAND_COLORS} layout="stacked"/>
                                        </Box>
                                    ))}
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </IndividualDashboardShell>
    );
};

export default ScoreOverviewPage;
